import { useTranslation } from 'react-i18next';
import { THEMES } from '@/lib/theme';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * ThemeSwitcher — segmented System / Light / Dark control.
 *
 * Shared by both settings surfaces (web Account page + desktop Account
 * screen). Renders as a <span> so it is legal inside the desktop settings
 * row's <p> subtitle slot.
 *
 * Translations land in a later task; every label carries an English default so
 * the control reads correctly until then (and in tests, where i18n may not be
 * initialised at all).
 */

const FALLBACK_LABELS = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

// font-medium is constant across both states — only color/border/background
// distinguish active from inactive, so switching the selection never reflows
// the button widths (a prior font-semibold-only-on-active variant did).
const BASE = 'px-3 py-1.5 text-sm rounded-lg border transition-colors font-medium';
const ACTIVE = 'border-brand-blue bg-brand-light text-brand-blue';
const INACTIVE = 'border-gray-300 text-gray-700 hover:bg-gray-50';

export default function ThemeSwitcher() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  return (
    <span
      role="group"
      aria-label={t('common:theme.label', 'Theme')}
      className="inline-flex flex-wrap items-center gap-2"
    >
      {THEMES.map((option) => {
        const active = theme === option;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            onClick={() => setTheme(option)}
            className={`${BASE} ${active ? ACTIVE : INACTIVE}`}
          >
            {t(`common:theme.${option}`, FALLBACK_LABELS[option])}
          </button>
        );
      })}
    </span>
  );
}
